import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'
import React from 'react'
import { Attributes } from '../components/Attributes'
import { BarelyVisibleTitle } from '../components/BarelyVisibleTitle'
import { Breadcrumbs } from '../components/Breadcrumbs'
import { CompositionsLayout } from '../components/CompositionsLayout'
import { Documents } from '../components/Documents'
import { Gallery } from '../components/Gallery'
import { MissingContent } from '../components/MissingContent'
import { useIsInModal } from '../components/ModalManager'
import { Parameters } from '../components/Parameters'
import { ProductLayout } from '../components/ProductLayout'
import { References } from '../components/References'
import { Seo } from '../components/Seo'
import { Spacer } from '../components/Spacer'
import { VideoList } from '../components/VideoList'
import { WidthBound } from '../components/WidthBound'
import { useCurrentLocale } from '../src/contexts/CurrentLanguageContext'
import { useTranslate } from '../src/hooks/useTranslate'
import {
	Align,
	CompositionFragment,
	DocumentListFragment,
	ImageListFragment,
	LinkableFragment,
	Model3dFragment,
	ProductLocaleFragment,
	ProductUnit,
	ProductVariantAttributesFragment,
	ProductVariantFragment,
	VideoFragment,
	ProductTypeOrganizationFragment,
	ProductFragment,
	ProductRackFragment,
	ReferenceListFragment,
	ProductTypeFragment,
	UUID,
} from '../src/model/fragments'
import { getLocale, getLocaleUrlPrefix, getPossibleLocales, Locale } from '../src/model/locale'
import { getProductCategoryLink, getProductLink } from '../src/model/urls'
import { fetchGet } from '../src/utils/fetchWrapper'
import { formatDimensions } from '../src/utils/formatDimensions'
import { isDefined } from '../src/utils/isDefined'
import { useSelectedVariantId } from '../src/utils/useSelectedVariantId'
import { ProductList, ProductListKind } from '../components/ProductList'
import { ProductRackView } from '../components/ProductRackView'
import { PillsProps } from '../components/Pills'

export default function Product({
	product,
	locales,
	variantId
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
	const translate = useTranslate()
	const locale = useCurrentLocale()
	const isInModal = useIsInModal()

	const [selectedVariantId, setSelectedVariantId] = useSelectedVariantId(
		product?.id || '',
		variantId ?? (product && product.variants.length === 1 ? product.variants[0].id : null)
	)

	React.useEffect(() => {

		if(variantId)
		{
			setSelectedVariantId(variantId);
		}

	}, []);
	

	if (!product) {
		return <MissingContent />
	}

	const data = getLocale<ProductLocaleFragment>(product.locales, locales)

	if (!data) {
		return <MissingContent />
	}

	const variant =
		selectedVariantId === null
			? null
			: (() => {
					const item = product.variants.find(
						(variant) => variant.id === selectedVariantId,
					)
					return item || null
			  })()
	const firstVariant = product.variants[0]

	React.useEffect(() => {

		const localePrefix = getLocaleUrlPrefix(locales[0]);
		if(variant)
		{
			const data = getLocale(variant.locales, locales);
			if(data)
			{
				const target = localePrefix + product.link.url + '/' + data.slug + window.location.search + window.location.hash;
				window.history.replaceState(window.history.state, '', target);
			}	
		}
		else 
		{
			const target = localePrefix + product.link.url + window.location.search + window.location.hash;
			window.history.replaceState(window.history.state, '', target);
		}

	}, [selectedVariantId]);

	const samplerImages = (variant ? [variant] : product.variants)
		.map((variant) => variant.sampler?.image)
		.filter(isDefined)

	const variantImages =
		variant?.images?.items.map((item) => item.image).filter(isDefined) || []

	const galleryItems = (() => {
		const [firstImage, ...otherImages] =
			product.images?.items.map((x) => x.image).filter(isDefined) || []

		const model3d = product.model3d;
		if(model3d && !model3d.texture && samplerImages.length)
		{
			// If we have no texture, let's use the one from samplers
			model3d.texture = samplerImages[0];
		}

		return [
			...(firstImage ? [firstImage] : []),
			...(selectedVariantId ? samplerImages.map((image) => ({ ...image, cover: true })) : []),
			...otherImages,
			...variantImages,
			...(model3d ? [model3d] : [])
		]
	})()

	const getAttribute = <K extends keyof (ProductVariantAttributesFragment)>(
		key: K,
	): ProductVariantAttributesFragment[K] | undefined => (variant ?? firstVariant)?.attributes[key] ?? undefined

	const compositions = product.compositions
		.map((item) => item.composition)
		.filter(isDefined)

	const racks = React.useMemo(() => {
		if(!variant)
		{
			return product.racks;
		}

		return product.racks.filter(r => r.variantIds!.includes(variant.id));
	}, [product, variant]);

	const documents = product.documents?.items || []
	const productLink = getProductLink(
		locale,
		product.id,
		product.link?.url || '',
	)

	const root = product.productTypes.ancestors.length ? product.productTypes.ancestors[product.productTypes.ancestors.length - 1] : null;
	const customizedSeo = {
		...data.seo,
		titlePostfix: root ? getLocale(root.locales, locales)?.name : undefined,
		description: data.seo.description ?? data.description,
		keywords: data.seo.keywords ?? ([product.productTypes.default, ...product.productTypes.ancestors, product.productTypes.system, ...product.productTypes.additional].filter(t => t).map(t => getLocale(t!.locales, locales)?.name)).filter(k => k) as string[],
	}

	const reversedAncestors = Array.from(product.productTypes.ancestors).reverse();
	return (
		<>
			<Seo seo={customizedSeo} />
			{!isInModal && (
				<Breadcrumbs
					items={[
						...([...reversedAncestors, product.productTypes.default].filter(t => t && t.link) as ProductTypeFragment[]).map(t => {
							const data = getLocale(t.locales, locales)
							return {
								label: data?.name || '',
								linkProps: getProductCategoryLink(locale, t.id, t.link.url),
							}
						}),
						{
							label: data.name,
						},
					]}
					sideItems={product.productTypes.system?.link ? [{
						label: getLocale(product.productTypes.system.locales, locales)?.name || '',
						linkProps: getProductCategoryLink(locale, product.productTypes.system.id, product.productTypes.system.link.url),
					}] : []}
				/>
			)}

			<Spacer initial={10} />
			<ProductLayout
				id={product.id}
				name={data.name}
				altName={data.altName}
				unit={product.attributes?.unit}
				pills={[
					product.references.items.length ? {
						label: translate('references-label'),
						anchor: 'References'
					} : {},
					documents.length ? {
						label: translate('documents-label'),
						anchor: 'Documents'
					} : {},
					compositions.length ? {
						label: translate('compositions-label'),
						anchor: 'Compositions'
					} : {},
					data.videos.length ? {
						label: translate('videos-label'),
						anchor: 'Videos'
					} : {},
					racks.length ? {
						label: translate('productRacks-productsLabel'),
						anchor: 'Racks'
					} : {},
					product.related.length ? {
						label: translate('related-products'),
						anchor: 'Related'
					} : {},
				].filter(it => Object.keys(it).length) as PillsProps['pills']}
				quantityPerLayer={
					(variant || firstVariant)?.attributes.palletLayerQuantity ||
					0
				}
				quantityPerSqm={
					(variant || firstVariant)?.attributes.quantityPerSqm || 0
				}
				palletLayerSqm={
					(variant || firstVariant)?.attributes.palletLayerSqm || 0
				}
				description={data.description}
				complementaryDimensions={data.complementaryDimensions}
				productTypes={product.productTypes}
				attributes={
					<Attributes
						walkable={product.attributes?.walkable}
						rideableByCar={product.attributes?.rideableByCar}
						rideableByTruck={product.attributes?.rideableByTruck}
						frostResistant={product.attributes?.frostResistant}
						antiSlip={product.attributes?.antiSlip}
						euNorms={product.attributes?.euNorms}
						withoutChamfer={product.attributes?.withoutChamfer}
						visibleEdge={product.attributes?.visibleEdge}
						customMade={product.attributes?.customMade}
						impregnation={getAttribute('impregnation')}
					/>
				}
				dimensions={formatDimensions(
					product.attributes?.length,
					product.attributes?.width,
					product.attributes?.height,
				)}
				parameters={
					<Parameters
						id={variant?.externalIdentifier || 'vyberte variantu'}
						palletType={getAttribute('palletType')}
						weightPerSquareMeter={getAttribute('weightPerSqm')}
						quantityPerSqm={getAttribute('quantityPerSqm')}
						squareMeterPerPallet={getAttribute(
							'squareMeterPerPallet',
						)}
						quantityPerPallet={getAttribute('quantityPerPallet')}
						palletLayerSquareMeter={getAttribute('palletLayerSqm')}
						palletLayerQuantity={getAttribute('palletLayerQuantity')}
						palletWeight={getAttribute('palletWeight')}
						unit={product.attributes?.unit}
					/>
				}
				gallery={
					galleryItems.length ? (
						<Gallery label={(data.name + ' ' + product.dimensionsString).trim()} items={galleryItems}></Gallery>
					) : undefined
				}
				variants={product.variants}
				selectedVariantId={selectedVariantId}
				onSelectedVariantIdChange={setSelectedVariantId}
			/>
			
			{ product.references.items.length > 0 && 
				<div id="References">
				<Spacer initial={60} />
				<WidthBound>
					<BarelyVisibleTitle line align={Align.left} level={2}>
						{translate('references-label')}
					</BarelyVisibleTitle>
					<References
						/* @TODO: make references linkable */
						linkToItem={(itemId: string | null) => productLink}
						items={product.references.items.map((item) => {
							const data = getLocale(item.locales, locales)

							return {
								id: item.id,
								name: data?.title || '',
								image: item.image,
								listingImage: item.listingImage,
								products: item.products,
								linkProps: productLink,
							}
						})}>
						</References>
				</WidthBound>
				</div>
			}
			
			
			{ documents.length > 0 && 
				<div id="Documents">
				<Spacer initial={100} />
				<WidthBound>
					<BarelyVisibleTitle line level={2} align={Align.left}>{translate('documents-label')}</BarelyVisibleTitle>
					<Spacer initial={24} />
					<Documents
						items={documents
							.map((x) => x.document)
							.filter(isDefined)}
					/>
				</WidthBound>
				</div>
			}

			{ compositions.length > 0 && 
				<div id="Compositions">
					<Spacer initial={100} />
					<WidthBound>
						<BarelyVisibleTitle line level={2} align={Align.left}>
							{translate('compositions-label')}
						</BarelyVisibleTitle>
						<p>
							{translate('compositions-description')}
						</p>
						<Spacer initial={24} />
						
						<CompositionsLayout
							compositions={compositions}
						/>
					</WidthBound>
					
				</div>
			}


			{ data.videos.length > 0 && 
				<div id="Videos">
					<Spacer initial={120} />
					<WidthBound>
						<BarelyVisibleTitle level={2} align={Align.left}>
							{translate('videos-label')}
						</BarelyVisibleTitle>
						<Spacer initial={24} />
						<VideoList grid={true} items={data.videos} />
					</WidthBound>
				</div>
			}

			{
				racks.length > 0 &&
				<div id="Racks">
					<Spacer initial={120} />
					<WidthBound>
						<BarelyVisibleTitle level={2} align={Align.left} line>
							{translate('productRacks-productsLabel')}
						</BarelyVisibleTitle>
						<p>
							{translate('productRacks-description')}<br/>
							<span className='productRacks-cta'>{translate('productRacks-cta')}</span>
						</p>
						<Spacer initial={64} />
						<ProductRackView racks={racks} />
						<Spacer initial={24} />
						
					</WidthBound>
				</div>
			}

			{ product.related.length > 0 &&
				<div id="Related">
					<Spacer initial={120} />
					<WidthBound>
						<BarelyVisibleTitle level={2} align={Align.left} line>
							{translate('related-products')}
						</BarelyVisibleTitle>
						<p>
							{translate('related-products-description')}:
						</p>
						<Spacer initial={24} />
						<ProductList
							items={product.related}
							kind={ProductListKind.Simple}
							highlightUnselectedVariantPickers={false}
						/>
						<Spacer initial={24} />
						
					</WidthBound>
				</div>
			}

			<Spacer initial={100} />
		</>
	)
}

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
	const locale = ctx.query.locale as Locale
	const locales = getPossibleLocales(locale)
	const { getProduct: product } = await fetchGet('/products/detail', new URLSearchParams({locale: locale, productId: ctx.query.id?.toString() ?? ''})) as 
	{
		getProduct: {
			id: UUID,
			dimensionsString: string,
			link: LinkableFragment,
			attributes: {
				length: number,
				width: number,
				height: number,
				unit: ProductUnit,
				walkable: boolean,
				rideableByCar: boolean,
				rideableByTruck: boolean,
				frostResistant: boolean,
				antiSlip: boolean,
				euNorms: boolean,
				withoutChamfer: boolean,
				visibleEdge: boolean,
				customMade: boolean,
			} 
			images: ImageListFragment,
			model3d: Model3dFragment | null,
			productTypes: ProductTypeOrganizationFragment,
			documents: DocumentListFragment,
			videoList: VideoFragment[],
			references: ReferenceListFragment,
			compositions: {
				composition: CompositionFragment
			}[],
			newProduct: boolean,
			locales: ProductLocaleFragment[],
			variants: ProductVariantFragment[],
			related: ProductFragment[],
			racks: ProductRackFragment[]
		} 
	};

	const variantId = ctx.query.variantId as string;
	return {
		props: {
			product: product,
			locales,
			modal: false,
			variantId: variantId ?? null,
		},
	}
}
